import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  TabMenuContainer,
  EmptySettingsDetails,
  TabSearchContainer,
  PopoverContainer,
  ClearAll,
  NoteText,
  TabMenuHeaderContainer,
  TabMenuItemContainer,
  SearchNavWrapper,
  TabMenuHeader,
  DropdownDetails,
  HelpGuideWrapper,
  LoaderContainer,
  NoDataView,
} from '../../containers/SidebarMenu/styles';
import Loader from '../../components/Loader';
import ComplexPreviewModal from '../../components/Modals/ComplexPreviewModal';
import EmptyElementsIllustration from '../../images/empty-elements.svg';
import { withTranslation } from 'react-i18next';
import { DESIGN_AREA_FLOW_BLOCK_TAB } from '../../containers/SidebarMenu/translationValues';
import { FilterSvg } from '../../components/SidebarMenuIcons';
import EditorElement from '../../components/EditorElement';
import AddTagsModal from '../../components/Modals/AddTagsModal';
import {
  Icon,
  Menu,
  Dropdown,
  Col,
  Input,
  Popover,
  Select,
  Button,
  Badge,
  Modal,
  message,
} from 'antd';
import { EMAIL_DESIGN_TYPE, REUSABLE_SECTION } from '../../utils/complexEmailConstants';
import {
  fetchAllStandaloneBlock,
  FetchReferencedBycategory,
  deleteReferencedBlock,
  fetchManageTags,
} from '../../utils/services/complexEmailAPI';
import CreateStandaloneReusableSection from '../../components/Modals/CreateStandaloneReusableSection';
import URL, { checkUserAccessRoles } from '../../utils/helper';
import { useSessionContext } from '../../context/sessionContext';

const generateEmailBlockThumbnail = id =>
  `${URL.DESIGN_SYSTEM_URL}email/standalone/block/thumbnail/${id}`;

const NAMESPACES = ['DESIGN_AREA_FLOW_BLOCK_TAB'];
const { Option } = Select;

const ComplexEmailElementsTab = props => {
  const { t } = props;

  const [selectedView, setSelectedView] = useState('view-templates');
  const [selectedTab, setSelectedTab] = useState('block');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [fetchingCategories, setFetchingCategories] = useState(true);
  const [updateModalVisibility, setUpdateModalVisibility] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState();
  const [loadMore, setLoadMore] = useState(false);
  const [page, setPage] = useState(1);
  const [defaultTemplates, setDefaultTemplates] = useState([]);
  const [searchKey, setSearchKey] = useState('');
  const [searchedTemplates, setSearchedTemplates] = useState({});
  const [noData, setNodata] = useState(false);
  const [filterVisible, setFilterVisible] = useState(false);
  const [filterOption, setFilterOption] = useState('All');
  const [categoryTemplates, setCategoryTemplates] = useState([]);
  const [isBottom, setIsBottom] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const tagList = useRef();

  const sessionSelector = useCallback(store => store.userData, []);
  const [userDetails] = useSessionContext(sessionSelector);

  const isReusableContentManager = checkUserAccessRoles(userDetails, ['REUSABLECONTENTMANAGER']);

  useEffect(() => {
    if (isBottom && !loadMore && !noData) {
      if (selectedView === 'view-search-template') {
        onSearch();
      }
    }
    setDefaultTemplates(categoryTemplates);
    // eslint-disable-next-line
  }, [isBottom, categoryTemplates]);

  const onScroll = e => {
    if (!setIsBottom) return;
    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
    if (Math.abs(scrollTop + clientHeight - scrollHeight) < 10) {
      setIsBottom(true);
    } else {
      setIsBottom(false);
    }
  };

  const onClose = () => {
    setSearchKey('');
    if (!selectedCategory) {
      updateSelectedView('view-templates', null);
    } else {
      onSearch(true, undefined, '');
    }
  };

  const seeAllByCategory = category => {
    if (category === 'All') {
      updateSelectedView('view-templates', null);
    } else {
      setSelectedCategory(category);
      onSearch(true, category);
    }
  };

  const updateSelectedView = (view, templateObj) => {
    if (view === 'view-templates') {
      setSearchKey('');
      setFilterOption('All');
      setSelectedCategory(undefined);
      setSearchedTemplates({});
    }
    setSelectedView(view);
    setSelectedTab(templateObj);
    handleTemplateType(selectedTab);
  };

  const onSearch = async (first = false, category = selectedCategory, searchParam = searchKey) => {
    let currentPage = page;
    if (first) {
      setPage(1);
      currentPage = 1;
      setNodata(false);
      setFetchingCategories(true);
    }
    const payload = {
      name: searchParam || undefined,
      categoryId: category?.categoryId || undefined,
    };
    if (searchParam !== '') {
      payload.searchParam = searchKey || undefined;
    }
    setSelectedView('view-search-template');
    setLoadMore(true);
    setFilterOption(category);
    const response = await fetchAllStandaloneBlock(null, null, category?.categoryId, searchParam);
    const statusCode = [2605, 2505];
    if (statusCode.includes(response?.statusCode)) {
      const templates = response.data;
      let updatedSearchTemplates = {
        count: response.dataCount,
        templates: first ? templates : [...searchedTemplates?.templates, ...templates],
      };
      setSearchedTemplates(updatedSearchTemplates);
      if (payload.limit * currentPage >= response.dataCount) {
        setNodata(true);
      }
      setPage(currentPage + 1);
    } else {
      message.warn(
        t(
          DESIGN_AREA_FLOW_BLOCK_TAB.FAILED_TO_PERFORM_ERROR_MESSAGE.key,
          DESIGN_AREA_FLOW_BLOCK_TAB.FAILED_TO_PERFORM_ERROR_MESSAGE.value,
        ),
      );
    }
    setFetchingCategories(false);
    setLoadMore(false);
  };

  const fetchAllBlockByCategory = async () => {
    const response = await FetchReferencedBycategory({ countPerCategory: 4 });
    if (response.statusCode === 2608) {
      setCategoryTemplates(response?.data ?? []);
      setDefaultTemplates(response?.data);
    } else {
      message.warn(
        t(
          DESIGN_AREA_FLOW_BLOCK_TAB.FAILED_TO_PERFORM_ERROR_MESSAGE.key,
          DESIGN_AREA_FLOW_BLOCK_TAB.FAILED_TO_PERFORM_ERROR_MESSAGE.value,
        ),
      );
    }
    setFetchingCategories(false);
  };

  useEffect(() => {
    (async () => {
      handleTemplateType('block');
    })();
  }, []);

  const renderEmptyView = () => (
    <EmptySettingsDetails>
      <div className="empty-illustration">
        <img src={EmptyElementsIllustration} alt="EmptyElementsIllustration" />
      </div>
      <div className="empty-txt">
        <span>
          {t(
            DESIGN_AREA_FLOW_BLOCK_TAB.TEMPLATES_LIST_EMPTY.key,
            DESIGN_AREA_FLOW_BLOCK_TAB.TEMPLATES_LIST_EMPTY.value,
          )}
        </span>
      </div>
    </EmptySettingsDetails>
  );

  const onSuccess = () => {
    setUpdateModalVisibility(false);
    if (selectedView === 'view-templates') fetchAllBlockByCategory();
    else if (selectedView === 'view-search-template') onSearch(true);
  };

  const handleDeletion = async () => {
    setIsDeleting(true);
    try {
      const response = await deleteReferencedBlock(selectedTemplate.id);
      if (response?.statusCode === 2506) {
        message.success(
          t(
            DESIGN_AREA_FLOW_BLOCK_TAB.BLOCK_DELETED_SUCCESS_MESSAGE.key,
            DESIGN_AREA_FLOW_BLOCK_TAB.BLOCK_DELETED_SUCCESS_MESSAGE.value,
          ),
        );
        onSuccess();
      } else if (response?.errors?.some(error => error.code === 2501)) {
        message.error(
          t(
            DESIGN_AREA_FLOW_BLOCK_TAB.BLOCK_CANNOT_DELETE_ERROR_MESSAGE.key,
            DESIGN_AREA_FLOW_BLOCK_TAB.BLOCK_CANNOT_DELETE_ERROR_MESSAGE.value,
          ),
        );
      } else {
        message.error(
          t(
            DESIGN_AREA_FLOW_BLOCK_TAB.BLOCK_DELETE_FAILED_ERROR_MESSAGE.key,
            DESIGN_AREA_FLOW_BLOCK_TAB.BLOCK_DELETE_FAILED_ERROR_MESSAGE.value,
          ),
        );
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsDeleting(false);
    }
  };

  const renderModals = () => {
    switch (selectedAction) {
      case 'create-reusableBlock':
        return (
          <CreateStandaloneReusableSection
            onCancel={disableModalVisibility}
            visible={updateModalVisibility}
            isEmailElements={true}
            isReferenced={true}
            isElementsTab={true}
          />
        );
      case 'delete-template':
        return (
          <Modal
            centered
            visible={updateModalVisibility}
            onCancel={disableModalVisibility}
            footer={[
              <Button onClick={disableModalVisibility}>
                {t(
                  DESIGN_AREA_FLOW_BLOCK_TAB.CANCEL_BUTTON.key,
                  DESIGN_AREA_FLOW_BLOCK_TAB.CANCEL_BUTTON.value,
                )}
              </Button>,
              <Button onClick={handleDeletion} loading={isDeleting}>
                {t(
                  DESIGN_AREA_FLOW_BLOCK_TAB.DELETE_BUTTON.key,
                  DESIGN_AREA_FLOW_BLOCK_TAB.DELETE_BUTTON.value,
                )}
              </Button>,
            ]}
            style={{ textAlign: 'center' }}
          >
            <h4>
              {t(
                DESIGN_AREA_FLOW_BLOCK_TAB.DELETE_CONFIRM_MESSAGE.key,
                DESIGN_AREA_FLOW_BLOCK_TAB.DELETE_CONFIRM_MESSAGE.value,
              )}{' '}
              <strong>{selectedTemplate.name}</strong>?
            </h4>
          </Modal>
        );
      case 'add-tag':
        return (
          <AddTagsModal
            visible={updateModalVisibility}
            onCancel={disableModalVisibility}
            target={{ id: selectedTemplate.id, elementType: EMAIL_DESIGN_TYPE.EMAIL_BLOCK, tags: tagList.current }}
            isComplex={true}
          />
        );
      case 'preview-thumbnail':
        return (
          <ComplexPreviewModal
            visible={updateModalVisibility}
            onCancel={disableModalVisibility}
            type={EMAIL_DESIGN_TYPE.EMAIL_BLOCK}
            templateId={selectedTemplate?.id || selectedTemplate?.parentBlockId}
          />
        );

      default:
        break;
    }
  };

  const enableModalVisibility = async (actionType, template) => {
    setUpdateModalVisibility(true);
    setSelectedTemplate(template);
    setSelectedAction(actionType);

    if (actionType === 'open_addTag_modal') {
      const payload = { id: template.id, type: EMAIL_DESIGN_TYPE.EMAIL_BLOCK };
      const fetchTagsResponse = await fetchManageTags(payload);
      if (fetchTagsResponse?.statusCode === 1901) {
        tagList.current = fetchTagsResponse.data.tags;
        setSelectedAction('add-tag');
      } else {
        message.error(
          t(
            DESIGN_AREA_FLOW_BLOCK_TAB.FETCHING_RECORDS_ERROR_MESSAGE.key,
            DESIGN_AREA_FLOW_BLOCK_TAB.FETCHING_RECORDS_ERROR_MESSAGE.value,
          ),
        );
      }
    }
  };

  const handleTemplateType = async key => {
    setSelectedTab(key);
    setSearchKey('');
    setFilterOption('All');
    setSelectedView('view-templates');
    setSearchedTemplates({});
    await fetchAllBlockByCategory();
  };

  const previewModalVisibility = (e, actionType, template, pageId) => {
    e.stopPropagation();
    setUpdateModalVisibility(true);
    setSelectedAction(actionType);
    setSelectedTemplate(template);
  };

  const renderDropDown = templateObj => {
    return (
      <DropdownDetails
        id={`${templateObj.id}-template`}
        className="dropdown-menu"
        onClick={e => e.stopPropagation()}
      >
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item
                key="1"
                onClick={() => enableModalVisibility('delete-template', templateObj)}
              >
                {t(
                  DESIGN_AREA_FLOW_BLOCK_TAB.TEMPLATE_DELETE_DROPDOWN_OPTION.key,
                  DESIGN_AREA_FLOW_BLOCK_TAB.TEMPLATE_DELETE_DROPDOWN_OPTION.value,
                )}
              </Menu.Item>
              <Menu.Item
                key="2"
                onClick={() => enableModalVisibility('open_addTag_modal', templateObj)}
              >
                {t(
                  DESIGN_AREA_FLOW_BLOCK_TAB.TEMPLATE_MANAGE_TAGS_DROPDOWN_OPTION.key,
                  DESIGN_AREA_FLOW_BLOCK_TAB.TEMPLATE_MANAGE_TAGS_DROPDOWN_OPTION.value,
                )}
              </Menu.Item>
            </Menu>
          }
          trigger={['click']}
          getPopupContainer={() => document.getElementById(`${templateObj.id}-template`)}
        >
          <Icon type="more" />
        </Dropdown>
      </DropdownDetails>
    );
  };

  const singleTemplate = (category, templateObj, action) => {
    let categoryByTemplate = {
      id: templateObj.id || templateObj.parentBlockId,
      name: templateObj.flowName || templateObj.parentBlockName,
      categoryName: category?.categoryName,
      categoryId: category?.categoryId,
      tags: templateObj.tags,
      targetTemplate: templateObj.flow,
    };

    let pageDetails = {
      language: templateObj?.language,
      thumbnail: templateObj?.thumbnail ? templateObj?.thumbnail : generateEmailBlockThumbnail(templateObj.parentBlockId),
      elementDetails: templateObj.flow || templateObj.blocks,
      isTemplate: true,
      name: templateObj.flowName || templateObj.parentBlockName,
      id: templateObj.id || templateObj.parentBlockId,
      ...(templateObj?.type && { sectionType: templateObj.type }),
      ...(templateObj?.style && {style: templateObj.style}),
      ...(templateObj?.variableContractDTOS && {
        variableContract: templateObj.variableContractDTOS,
      }),
    };
    return (
      <EditorElement
        element={pageDetails}
        setIsDragAndDropped={() => {}}
        clickElement={() => {}}
        toggleDragging={() => {}}
        type={REUSABLE_SECTION}
        cardType={'reusableTemplate'}
        renderDropDown={() => isReusableContentManager ? renderDropDown(categoryByTemplate) : null}
        onPreviewClick={e => previewModalVisibility(e, 'preview-thumbnail', templateObj)}
      />
    );
  };

  const tabHeaderView = category => {
    const template = category.standaloneBlocks;
    return (
      <Col>
        <TabMenuHeader inverted={0} className={'categoryName'}>
          <h2>{category.categoryName}</h2>
        </TabMenuHeader>
        {template?.length >= 4 && (
          <TabMenuHeader inverted={0} onClick={() => seeAllByCategory(category)}>
            <div className="see-all">
              {t(
                DESIGN_AREA_FLOW_BLOCK_TAB.SEE_ALL_TEMPLATES_ACTION.key,
                DESIGN_AREA_FLOW_BLOCK_TAB.SEE_ALL_TEMPLATES_ACTION.value,
              )}
            </div>
          </TabMenuHeader>
        )}
      </Col>
    );
  };

  const templateCardView = (category, action) => {
    const template = category.standaloneBlocks;
    return template?.length > 0 ? (
      <TabMenuItemContainer>
        {template.map(templateObj => {
          return singleTemplate(category, templateObj, action);
        })}
      </TabMenuItemContainer>
    ) : (
      <div></div>
    );
  };

  const showMoreView = text => {
    const loaderStyles = { top: '0px' };
    return (
      <>
        {loadMore && renderLoader(loaderStyles)}
        {noData && <NoDataView small>{text}</NoDataView>}
      </>
    );
  };

  const renderSearchView = () => {
    return searchedTemplates.templates?.length > 0 ? (
      <>
        <TabMenuItemContainer>
          {searchedTemplates.templates.map(data => {
            const categoryDetails = {
              categoryId: data.categoryId,
            };
            return singleTemplate(categoryDetails, data, 'view-search-template');
          })}
        </TabMenuItemContainer>
        {showMoreView(
          t(
            DESIGN_AREA_FLOW_BLOCK_TAB.GOT_ALL_RESULTS.key,
            DESIGN_AREA_FLOW_BLOCK_TAB.GOT_ALL_RESULTS.value,
          ),
        )}
      </>
    ) : (
      <NoDataView>
        {t(
          DESIGN_AREA_FLOW_BLOCK_TAB.NO_TEMPLATES_AVAILABLE.key,
          DESIGN_AREA_FLOW_BLOCK_TAB.NO_TEMPLATES_AVAILABLE.value,
        )}
      </NoDataView>
    );
  };

  const renderCategory = () => {
    return (
      <>
        {categoryTemplates.map(category => (
          <TabMenuHeaderContainer key={category.categoryId}>
            {tabHeaderView(category)}
            {templateCardView(category, 'view-templates')}
          </TabMenuHeaderContainer>
        ))}
      </>
    );
  };

  const renderTabBody = () => {
    switch (selectedView) {
      case 'view-templates':
        return renderCategory();
      case 'view-search-template':
        return renderSearchView();
      default:
        break;
    }
  };

  const renderSearchBar = () => {
    return (
      <TabSearchContainer>
        <Input
          name="templateSearch"
          placeholder={t(
            DESIGN_AREA_FLOW_BLOCK_TAB.SEARCH_TEMPLATES_PLACEHOLDER.key,
            DESIGN_AREA_FLOW_BLOCK_TAB.SEARCH_TEMPLATES_PLACEHOLDER.value,
          )}
          value={searchKey}
          onChange={e => setSearchKey(e.target.value)}
          onPressEnter={() => onSearch(true)}
          style={{ width: 200 }}
          prefix={<Icon type="search" />}
          autoComplete={'off'}
          suffix={
            <>
              {searchKey.length > 0 && <Icon type="close" onClick={onClose} />}
              <Popover
                placement="bottomRight"
                title={t(
                  DESIGN_AREA_FLOW_BLOCK_TAB.SEARCH_BY_CATEGORY_LABEL.key,
                  DESIGN_AREA_FLOW_BLOCK_TAB.SEARCH_BY_CATEGORY_LABEL.value,
                )}
                trigger="click"
                visible={filterVisible}
                onVisibleChange={() => setFilterVisible(!filterVisible)}
                content={
                  <PopoverContainer>
                    <ClearAll
                      onClick={() => [
                        setFilterVisible(false),
                        updateSelectedView('view-templates', null),
                      ]}
                    >
                      {t(
                        DESIGN_AREA_FLOW_BLOCK_TAB.CLEAR_ALL_FILTER_ACTION.key,
                        DESIGN_AREA_FLOW_BLOCK_TAB.CLEAR_ALL_FILTER_ACTION.value,
                      )}
                    </ClearAll>
                    <Select
                      showSearch
                      value={JSON.stringify(filterOption)}
                      style={{ width: 200 }}
                      placeholder={t(
                        DESIGN_AREA_FLOW_BLOCK_TAB.SELECT_CATEGORY_PLACEHOLDER.key,
                        DESIGN_AREA_FLOW_BLOCK_TAB.SELECT_CATEGORY_PLACEHOLDER.value,
                      )}
                      filterOption={(input, option) =>
                        option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                      }
                      onChange={value => setFilterOption(JSON.parse(value))}
                    >
                      <Option key="all" value={JSON.stringify('All')}>
                        {t(
                          DESIGN_AREA_FLOW_BLOCK_TAB.ALL_CATEGORY_DROPDOWN_OPTION.key,
                          DESIGN_AREA_FLOW_BLOCK_TAB.ALL_CATEGORY_DROPDOWN_OPTION.value,
                        )}
                      </Option>
                      {defaultTemplates?.length > 0 &&
                        defaultTemplates.map(data => (
                          <Option key={data.categoryId} value={JSON.stringify(data)}>
                            {data.categoryName}
                          </Option>
                        ))}
                    </Select>
                    <Button
                      type="primary"
                      onClick={() => [setFilterVisible(false), seeAllByCategory(filterOption)]}
                    >
                      {t(
                        DESIGN_AREA_FLOW_BLOCK_TAB.APPLY_FILTERS_BUTTON.key,
                        DESIGN_AREA_FLOW_BLOCK_TAB.APPLY_FILTERS_BUTTON.value,
                      )}
                    </Button>
                  </PopoverContainer>
                }
              >
                <Badge dot>
                  <Icon component={FilterSvg} onClick={() => setFilterVisible(!filterVisible)} />
                </Badge>
              </Popover>
            </>
          }
        />
        {!fetchingCategories && selectedView === 'view-search-template' && (
          <NoteText>
            {t(
              DESIGN_AREA_FLOW_BLOCK_TAB.TEMPLATES_FOUND_COUNT.key,
              DESIGN_AREA_FLOW_BLOCK_TAB.TEMPLATES_FOUND_COUNT.value,
              { templatesCount: searchedTemplates?.count },
            )}
          </NoteText>
        )}
      </TabSearchContainer>
    );
  };

  const renderLoader = (styles = {}) => (
    <LoaderContainer top={styles.top}>
      <Loader />
      <div className="loading-txt">
        {t(
          DESIGN_AREA_FLOW_BLOCK_TAB.LOADING_MORE_TEMPLATES_TEXT.key,
          DESIGN_AREA_FLOW_BLOCK_TAB.LOADING_MORE_TEMPLATES_TEXT.value,
        )}
      </div>
    </LoaderContainer>
  );

  const disableModalVisibility = () => {
    setUpdateModalVisibility(false);
    setSelectedAction(null);
  };

  return (
    <TabMenuContainer style={{ marginTop: selectedView === 'view-templates' ? '25px' : '75px' }}>
      <SearchNavWrapper className="hideSearch">{renderSearchBar()}</SearchNavWrapper>
      {fetchingCategories && renderLoader()}
      <div>
        {!fetchingCategories && (
          <div onScroll={onScroll}>
            {categoryTemplates?.length === 0 && renderEmptyView()}
            {categoryTemplates?.length > 0 && renderTabBody()}
          </div>
        )}
      </div>
      {isReusableContentManager && <HelpGuideWrapper reusable={true}>
        <div className="help-container">
          <div
            className="help-action-btn"
            onClick={() => enableModalVisibility('create-reusableBlock', null)}
          >
            <Icon type="plus" />{' '}
            {t(
              DESIGN_AREA_FLOW_BLOCK_TAB.CREATE_NEW_REUSABLE__SECTION_BUTTON.key,
              DESIGN_AREA_FLOW_BLOCK_TAB.CREATE_NEW_REUSABLE__SECTION_BUTTON.value,
            )}
          </div>
        </div>
      </HelpGuideWrapper>}
      {!fetchingCategories && updateModalVisibility && selectedAction && renderModals()}
    </TabMenuContainer>
  );
};

export default withTranslation(NAMESPACES)(ComplexEmailElementsTab);
